/* global window */

import { Base64 } from 'js-base64';
import NonceGenerator from 'a-nonce-generator';
import { sha256 } from 'js-sha256';

/**
 * Format message header.
 * @param {String} apiKey - API key.
 * @param {String} topic - Topic to publish message.
 * @param {String} epoch - Optional epoch-based timestamp.
 * @returns {String}
 */
export function getHeader(apiKey, topic, epoch = Date.now()) {
  const header = {
    apikey: apiKey,
    tpc: topic,
    epoch,
  };

  const headerJson = JSON.stringify(header);
  const headerBase64 = Base64.encode(headerJson);
  return headerBase64;
}

/**
 * Return a nonce.
 * @returns {Integer}
 */
function getNonce() {
  const ng = new NonceGenerator();
  const nonce = ng.generate();
  return nonce;
}

/**
 * Generate a message composed of concatenated header, message body and nonce.
 * @param {String} header - API key.
 * @param {String} messageBody - Topic to publish message.
 * @param {Integer} nonce - Optional single-use arbitrary integer.
 * @returns {String}
 */
export function getCandidateMessage(header, messageBody, nonce = getNonce()) {
  const message = `${header}.${messageBody}.${nonce}`;
  return message;
}

/**
 * Return the number of leading zeroes in target string.
 * @param {String} targetString - String to be checked.
 * @returns {Integer}
 */
export function getNumberOfLeadingZeroes(targetString) {
  let i = 0;

  for (i = 0; i < targetString.length; i++) {
    if (targetString[i] !== '0') {
      break;
    }
  }

  return i;
}

/**
 * Create formatted message for topic publishing.
 * Message follows a "aaa.bbb.ccc" pattern, where:
 * "aaa" is the Base64-encoded header;
 * "bbb" is the Base64-encoded message body;
 * "ccc" is a nonce that generates a SHA-256 hash of the message with target difficulty;
 * @param {String} body - Message body, serialized JSON.
 * @param {String} apiKey - API key.
 * @param {String} topic - Topic to publish message.
 * @param {Integer} difficulty - SHA-256 hash difficulty (number of leading zeroes).
 */
export function getPublishedMessageFormat(body, apiKey, topic, difficulty) {
  const header = getHeader(apiKey, topic);
  const messageBody = Base64.encode(body);

  let candidate = getCandidateMessage(header, messageBody);
  let candidateHash = sha256(candidate);
  let numLeadingZeroes = getNumberOfLeadingZeroes(candidateHash);

  while (numLeadingZeroes !== difficulty) {
    candidate = getCandidateMessage(header, messageBody);
    candidateHash = sha256(candidate);
    numLeadingZeroes = getNumberOfLeadingZeroes(candidateHash);
  }

  return candidate;
}

window.Metaphor = window.Metaphor || {};
window.Metaphor.getPublishedMessageFormat = getPublishedMessageFormat;



// WEBPACK FOOTER //
// ./src/message.js